import styled, { css } from 'styled-components';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { ReadOnlyBanner, useAgentPermission } from '@entities/user';
import { useGetCeoDashboardQuery } from '@shared/api';
import { riseIn } from '@shared/theme/animations';
import { IconBarChart } from '@shared/ui/icons';
import { formatMillions, formatDateTimeRu } from './ceoFormat';

const Shell = styled.div`
  max-width: 1360px;
  width: 100%;
  min-width: 0;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(5)};
  box-sizing: border-box;
`;

const Hero = styled.section`
  position: relative;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.radius.xl};
  background: linear-gradient(
    135deg,
    rgba(${({ theme }) => theme.colors.tint.base}, 0.18) 0%,
    rgba(42, 28, 21, 0.22) 52%,
    rgba(${({ theme }) => theme.colors.tint.base}, 0.12) 100%
  );
  backdrop-filter: blur(22px) saturate(160%);
  -webkit-backdrop-filter: blur(22px) saturate(160%);
  border: 1px solid rgba(${({ theme }) => theme.colors.tint.glow}, 0.32);
  box-shadow:
    0 16px 40px -18px rgba(0, 0, 0, 0.45),
    0 0 34px -22px rgba(${({ theme }) => theme.colors.tint.glow}, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  padding: ${({ theme }) => `${theme.spacing(6)} ${theme.spacing(7)}`};
  animation: ${riseIn} 420ms ${({ theme }) => theme.motion.easeOut} both;

  @media (max-width: 768px) {
    padding: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(4)}`};
  }
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(5)};
  flex-wrap: wrap;
`;

const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(4)};
  min-width: 0;
  max-width: 620px;

  /* На телефоне значок — над текстом: рядом с ним описанию остаётся узкая колонка. */
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Emblem = styled.div`
  flex-shrink: 0;
  width: 54px;
  height: 54px;
  border-radius: ${({ theme }) => theme.radius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  background: rgba(${({ theme }) => theme.colors.tint.base}, 0.12);
  border: 1px solid rgba(${({ theme }) => theme.colors.tint.glow}, 0.34);
`;

const Titles = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const Wordmark = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.text.primary};
  & .accent {
    color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const Tagline = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SyncMeta = styled.div`
  margin-top: 4px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LiveDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 8px rgba(16, 185, 129, 0.8);
`;

/** Сколько плашек влезает в ряд — столько и стоит: колонки считаются от ширины
 *  самой шапки, а не окна (слева ещё меню ОСНОВЫ, и окно о нём не знает). */
const StatStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(150px, 100%), 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
  width: 100%;
  min-width: 0;
`;

const Chip = styled.div`
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(3.5)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const ChipValue = styled.div`
  font-size: 17px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
`;

const ChipLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const ChipSub = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px;
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  overflow-x: auto;
  width: 100%;
  min-width: 0;
  -webkit-overflow-scrolling: touch;
`;

const Seg = styled(Link)<{ $active: boolean }>`
  padding: 9px 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition: all 160ms ease;
  color: ${({ theme, $active }) =>
    $active ? '#ffffff' : theme.colors.text.secondary};
  ${({ theme, $active }) =>
    $active
      ? css`
          background: ${theme.colors.primary};
          box-shadow: 0 4px 14px -4px rgba(${theme.colors.tint.base}, 0.55);
        `
      : css`
          background: transparent;
          &:hover {
            color: ${theme.colors.text.primary};
            background: ${theme.colors.backgroundElevated};
          }
        `}
`;

const CompactHero = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 44px;
  padding: 8px 16px;
  border-radius: ${({ theme }) => theme.radius.lg};
  background: linear-gradient(135deg, #09131f 0%, #0d1e33 60%, #102a45 100%);
  border: 1px solid rgba(56, 189, 248, 0.22);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  width: 100%;
  min-width: 0;

  @media (max-width: 640px) {
    flex-wrap: wrap;
    max-height: none;
    padding: 8px 12px;
    gap: 8px;
  }
`;

const CompactLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;
`;

const CompactTitle = styled.div`
  font-size: 15px;
  font-weight: 800;
  color: #f8fafc;
  display: flex;
  align-items: center;
  gap: 4px;

  .accent {
    color: #38bdf8;
  }
`;

const CompactRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  flex-wrap: wrap;
  min-width: 0;

  @media (max-width: 480px) {
    font-size: 11px;
    gap: 8px;
  }
`;

const CompactBadge = styled.span<{ $tone?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  background: rgba(16, 185, 129, 0.12);
  color: #10b981;
  border: 1px solid rgba(16, 185, 129, 0.3);
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(5)};
  width: 100%;
  min-width: 0;
`;

export function CeoLayout() {
  const location = useLocation();
  const { can_read, can_write } = useAgentPermission('ceo');
  const { data } = useGetCeoDashboardQuery();

  const segments = location.pathname.split('/').filter(Boolean); // ['agents', 'ceo', ...]
  const section = segments[2] ?? '';

  const accSum = data?.accounts_summary;
  const debtSum = data?.debt_summary;
  const infSum = data?.inflows_summary;
  const appSum = data?.payment_position;

  return (
    <Shell>
      {can_read && !can_write && <ReadOnlyBanner />}
      {section === '' ? (
        <Hero aria-label="ОСНОВА.Гендиректор">
          <HeroInner>
            <Identity>
              <Emblem aria-hidden>
                <IconBarChart size={26} />
              </Emblem>
              <Titles>
                <Wordmark>
                  {/* На телефоне название переносится после «ОСНОВА», а не обрезается. */}
                  ОСНОВА<wbr />
                  <span className="accent">.Гендиректор</span>
                </Wordmark>
                <Tagline>
                  Единый финансово-управленческий пульт холдинга: остатки на банковских счетах по API,
                  платежи за прошлый день, календарь и карточки кредитов и лизингов, сверка планов БДДС
                  с подписанными КС-3 и фактическими поступлениями.
                </Tagline>
                <SyncMeta>
                  <LiveDot />
                  <span>
                    API банков подключено: {accSum?.connected_api_count ?? 0} из{' '}
                    {accSum?.total_accounts_count ?? 0} счетов · Посл. синхронизация:{' '}
                    {formatDateTimeRu(accSum?.last_api_sync_at)} · Ключевая ставка ЦБ:{' '}
                    {debtSum ? `${debtSum.cbr_key_rate_pct.toFixed(1)}%` : '—'}
                  </span>
                </SyncMeta>
              </Titles>
            </Identity>

            <StatStrip>
              <Chip>
                <ChipLabel>Консолидированный остаток</ChipLabel>
                <ChipValue>
                  {accSum ? formatMillions(accSum.total_current_balance) : '—'}
                </ChipValue>
                <ChipSub>
                  Свободно: {accSum ? formatMillions(accSum.free_operational_cash) : '—'}
                </ChipSub>
              </Chip>
              <Chip>
                <ChipLabel>Вчера: приход / расход</ChipLabel>
                <ChipValue>
                  {data
                    ? `+${formatMillions(data.yesterday_payments.inflow_total)}`
                    : '—'}
                </ChipValue>
                <ChipSub>
                  Списано:{' '}
                  {data ? `−${formatMillions(data.yesterday_payments.outflow_total)}` : '—'}
                </ChipSub>
              </Chip>
              <Chip>
                <ChipLabel>Кредиты и лизинг (30 дн.)</ChipLabel>
                <ChipValue>
                  {debtSum ? formatMillions(debtSum.payments_next_30d) : '—'}
                </ChipValue>
                <ChipSub>
                  Портфель: {debtSum ? formatMillions(debtSum.total_debt_burden) : '—'}
                </ChipSub>
              </Chip>
              <Chip>
                <ChipLabel>КС-3 подписано / Факт</ChipLabel>
                <ChipValue>
                  {infSum ? formatMillions(infSum.total_actual_received) : '—'}
                </ChipValue>
                <ChipSub>
                  Ждём по КС-3: {infSum ? formatMillions(infSum.ks3_awaiting_cash) : '—'}
                </ChipSub>
              </Chip>
            </StatStrip>
          </HeroInner>
        </Hero>
      ) : (
        <CompactHero aria-label="ОСНОВА.Гендиректор (компактный вид)">
          <CompactLeft>
            <Emblem style={{ width: 32, height: 32, borderRadius: 8 }} aria-hidden>
              <IconBarChart size={18} />
            </Emblem>
            <CompactTitle>
              ОСНОВА<span className="accent">.Гендиректор</span>
            </CompactTitle>
            <CompactBadge>
              Свободный кэш: {accSum ? formatMillions(accSum.free_operational_cash) : '—'}
            </CompactBadge>
          </CompactLeft>
          <CompactRight>
            <span style={{ color: '#cbd5e1' }}>
              Консолидировано: <b style={{ color: '#f8fafc' }}>{accSum ? formatMillions(accSum.total_current_balance) : '—'}</b>
            </span>
            <span style={{ color: '#cbd5e1' }}>
              API банков: <b style={{ color: '#38bdf8' }}>{accSum?.connected_api_count ?? 0}/{accSum?.total_accounts_count ?? 0}</b>
            </span>
          </CompactRight>
        </CompactHero>
      )}

      <Nav aria-label="Разделы консоли Генерального директора">
        <Seg to="/agents/ceo" $active={section === ''}>
          Обзор
        </Seg>
        <Seg to="/agents/ceo/accounts" $active={section === 'accounts'}>
          Счета и выписки
        </Seg>
        <Seg to="/agents/ceo/debts" $active={section === 'debts'}>
          Кредиты и лизинг
        </Seg>
        <Seg to="/agents/ceo/inflows" $active={section === 'inflows'}>
          Поступления (КС-3)
        </Seg>
        <Seg to="/agents/ceo/approvals" $active={section === 'approvals'}>
          Акцепт платежей
          {appSum && appSum.pending_approvals_total > 0
            ? ` (${formatMillions(appSum.pending_approvals_total)})`
            : ''}
        </Seg>
        <Seg to="/agents/finance" $active={false} style={{ marginLeft: 'auto' }}>
          ОСНОВА.Финансист →
        </Seg>
        <Seg to="/agents/progress" $active={false}>
          Дашборд объектов →
        </Seg>
      </Nav>

      <Main>
        <Outlet />
      </Main>
    </Shell>
  );
}
